import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
/**
 * This function is used to analyze PDP paths and add additional context to the transactions sent to Sentry.
 * The PDP pattern is /[locale?]/p/[category]/[name]/_/[pid]
 * @param event Sentry Event
 * @param splitPath Array of the path split by "/"
 * @param pIndex Index of where /p/ is in the path
 * @returns {*&{tags: (*&{productId: *, category: *, locale: (*|string)})}}
 */ export function handlePdpPath(event, splitPath, pIndex) {
    var categoryIndex = pIndex + 1;
    var productIdIndex = pIndex + 4;
    return _object_spread_props(_object_spread({}, event), {
        tags: _object_spread_props(_object_spread({}, event.tags), {
            category: splitPath[categoryIndex],
            productId: splitPath[productIdIndex]
        })
    });
}
/**
 * This function is used to analyze CDP paths and add additional context to the transactions sent to Sentry.
 * The CDP pattern is /[locale?]/c/[id]/_/[nValue]
 * @param event Sentry Event
 * @param splitPath Array of the path split by "/"
 * @param cIndex Index of where /c/ is in the path
 * @returns {*&{tags: (*&{locale: (*|string), categoryId: *})}}
 */ export function handleCdpPath(event, splitPath, cIndex) {
    var categoryIdIndex = cIndex + 1;
    return _object_spread_props(_object_spread({}, event), {
        tags: _object_spread_props(_object_spread({}, event.tags), {
            categoryId: splitPath[categoryIdIndex]
        })
    });
}
/**
 * This function is used to add additional context to the transactions sent to Sentry.
 * It is called before the transaction is sent and allows for deeper analysis of each transaction.
 * Since we group all transactions under the same name, this uses the path to create tags for each transaction,
 * which allows us to analyse products or categories that are slower, as an examples
 * @param event Sentry Event
 * @returns {*|(*&{tags: (*&{locale: (string|string), categoryId: string})})|(*&{tags: (*&{productId: string, category: string, locale: (string|string)})})}
 */ export function beforeSendTransaction(event) {
    if ("object" === "undefined") {
        return event;
    }
    var splitPath = location === null || location === void 0 ? void 0 : location.pathname.split("/");
    /**
   * Find the regionalized routes in the path and remove them to group it into a single transaction.
   * For `fr.shop.lululemon.com` we also check for the fr.shop domain.
   */ if (splitPath.indexOf("en-ca") > -1) {
        event.tags.locale = "en-ca";
        event.transaction = event.transaction.replace("/en-ca", "");
    } else if (splitPath.indexOf("fr-ca") > -1 || location.hostname.includes("fr.shop")) {
        event.tags.locale = "fr-ca";
        event.transaction = event.transaction.replace("/fr-ca", "");
    }
    // Handle PDP
    var pIndex = splitPath.indexOf("p");
    if (pIndex > -1) {
        return handlePdpPath(event, splitPath, pIndex);
    }
    // Handle CDP
    var cIndex = splitPath.indexOf("c");
    if (cIndex > -1) {
        return handleCdpPath(event, splitPath, cIndex);
    }
    // No paths found, return as is
    return event;
}
/**
 * This is a map of regexes used to match different types of pages under ecom-web-app.
 * @type {{PDP_GIFTCARDS: RegExp, SEARCH: RegExp, PDP: RegExp, CDP: RegExp}}
 */ export var REGEXES = {
    PDP_GIFTCARDS: /\/p\/gift-cards\/(.+)/,
    PDP: /\/p\/(.+)/,
    CDP: /\/c\/(.+)/,
    SEARCH: /\/search/
};
/**
 * This function is used to sanitize the transaction name before sending it to Sentry.
 * It replaces the product name with a generic placeholder, so that all transactions are grouped together.
 * It also does the same for categories
 * @param pathname Pathname of the page
 * @returns {*|string} Sanitized pathname
 */ export function sanitizeTransactionName(pathname) {
    if (!pathname) return pathname;
    if (REGEXES.PDP_GIFTCARDS.test(pathname)) {
        return "/p/gift-cards/[name]/_/[pid]";
    } else if (REGEXES.PDP.test(pathname)) {
        return "/p/[category]/[name]/_/[pid]";
    } else if (REGEXES.CDP.test(pathname)) {
        return "/c/[id]/_/[nValue]";
    } else if (REGEXES.SEARCH.test(pathname)) {
        return "/search?ntt=[searchTerm]";
    }
    return pathname;
}
